import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { changeAcceptedStatus, getAcceptedStatusOptions } from "../../services/apiClient.js";

// The accepted sub-status machine (To Organise / In Progress / On Hold) —
// details.md §2. Only rendered while the claim is Accepted. This is an ACTION
// panel (like the Status tab), not part of the Edit form: applying a change
// fires the hold side effects server-side (hold dates, estimated-end extension,
// progress entry), so it saves on its own.

function AcceptedStatusPanel({ claim, onChanged }) {
  const [options, setOptions] = useState({ statuses: [], on_hold_reasons: [] });
  // An accepted job is never stateless — a blank value renders as the default
  // To Organise, exactly like legacy DetailsEdit (details.md 2.1).
  const [status, setStatus] = useState(claim.accepted_status || "To Organise");
  const [reason, setReason] = useState(claim.on_hold_reason || "");
  const [review, setReview] = useState(claim.on_hold_review_date ? String(claim.on_hold_review_date).slice(0, 10) : "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getAcceptedStatusOptions().then(setOptions).catch(() => {});
  }, []);

  useEffect(() => {
    setStatus(claim.accepted_status || "To Organise");
    setReason(claim.on_hold_reason || "");
    setReview(claim.on_hold_review_date ? String(claim.on_hold_review_date).slice(0, 10) : "");
  }, [claim]);

  const isRQ = String(claim.job_type_name || "").trim().toLowerCase() === "standard r&q";
  const inProgressBlocked = isRQ && !claim.actual_start_date;

  const dirty =
    status !== (claim.accepted_status || "") ||
    (status === "On Hold" &&
      (reason !== (claim.on_hold_reason || "") ||
        review !== (claim.on_hold_review_date ? String(claim.on_hold_review_date).slice(0, 10) : "")));

  async function apply() {
    setError("");
    setSaving(true);
    try {
      await changeAcceptedStatus(claim.id, {
        accepted_status: status,
        on_hold_reason: status === "On Hold" ? reason : null,
        on_hold_review_date: status === "On Hold" ? review || null : null,
      });
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to change the accepted status.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="border rounded p-3 mb-3">
      <div className="d-flex flex-wrap align-items-center column-gap-4 row-gap-2">
        <span className="text-uppercase fw-semibold small text-muted" style={{ letterSpacing: "0.06em" }}>
          Accepted Status
        </span>
        {options.statuses.map((s) => (
          <Form.Check
            inline
            key={s}
            type="radio"
            id={`acc-${s}`}
            name="accepted_status"
            label={s}
            checked={status === s}
            disabled={s === "In Progress" && inProgressBlocked}
            data-hint={
              s === "In Progress" && inProgressBlocked
                ? "Set the Actual Start Date before marking a Standard R&Q job In Progress."
                : undefined
            }
            onChange={() => setStatus(s)}
          />
        ))}
        <Button size="sm" onClick={apply} disabled={saving || !status || !dirty}>
          Apply
        </Button>
      </div>

      {status === "On Hold" ? (
        <div className="d-flex flex-wrap align-items-end column-gap-3 row-gap-2 mt-2">
          <div>
            <Form.Label className="mb-0 small fw-semibold">On Hold Reason</Form.Label>
            <ThemedSelect size="sm" value={reason} onChange={(e) => setReason(e.target.value)} style={{ width: 280 }}>
              <option value="">— Choose a reason —</option>
              {options.on_hold_reasons.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </ThemedSelect>
          </div>
          <div>
            <Form.Label className="mb-0 small fw-semibold">Review Date</Form.Label>
            <Form.Control size="sm" type="date" value={review} onChange={(e) => setReview(e.target.value)} style={{ width: 170 }} />
          </div>
          {claim.put_on_hold_date ? (
            <span className="small text-muted mb-1">
              On hold since {String(claim.put_on_hold_date).slice(0, 10)} — the estimated end date
              extends by the business days held when it comes off hold.
            </span>
          ) : null}
        </div>
      ) : null}

      {error ? <Alert variant="danger" className="py-1 px-2 mt-2 mb-0">{error}</Alert> : null}
    </div>
  );
}

export default AcceptedStatusPanel;
